<template>
  <BaseModal
    class="diet-nutrients-modal"
    :open="open"
    title="Najważniejsze składniki odżywcze"
    @update:open="!$event && emit('close')"
    @confirm="save"
  >
    <template #icon>
      <FeaturedIcon
        background-color="color-background-bg-primary"
        icon-color="color-text-text-secondary-700"
        name="Settings04"
        size="lg"
      />
    </template>
    <div class="diet-nutrients-modal__content">
      <nav aria-label="Kategorie składników odżywczych" class="diet-nutrients-modal__categories">
        <button
          v-for="category in nutrientsCategories"
          :key="category"
          class="diet-nutrients-modal__category"
          :class="{ active: category === selectedCategory }"
          :aria-current="category === selectedCategory ? 'page' : undefined"
          :data-test-id="`DietNutrientsModal__Category_${category}`"
          type="button"
          @click="selectedCategory = category"
        >
          <span>{{ category }}</span>
          <Badge v-if="selectedCount(category)" color="success" size="sm" type="rounded">
            {{ selectedCount(category) }}
          </Badge>
        </button>
      </nav>
      <div class="diet-nutrients-modal__list">
        <Tooltip
          v-for="nutrient in nutrientsInCategory"
          :key="nutrient"
          :disabled="selectedVisibleCount < 5 || isSelected(nutrient)"
          placement="top-start"
        >
          <template #trigger>
            <Checkbox
              :disabled="!isSelected(nutrient) && selectedVisibleCount >= 5"
              :label="nutrientsMap[nutrient].name"
              :model-value="isSelected(nutrient)"
              size="sm"
              @update:model-value="toggleNutrient(nutrient, $event)"
            />
          </template>
          Zaznaczono już maksymalną liczbę składników (5)
        </Tooltip>
      </div>
    </div>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'

  import { Badge, BaseModal, Checkbox, FeaturedIcon } from '@/components/ui'
  import Tooltip from '@/components/ui/Navigation/Tooltip.vue'
  import { nutrientsCategories, nutrientsMap } from '@/utils/nutrients'

  import type { Nutrients, NutrientsCategories } from '@/utils/nutrients'

  import { hiddenNutrients } from './constants'

  const props = defineProps<{
    chosen: Nutrients[]
    open: boolean
  }>()
  const emit = defineEmits<{
    close: []
    save: [nutrients: Nutrients[]]
  }>()

  const nutrientsOrder: Record<NutrientsCategories, Nutrients[]> = {
    'Składniki odżywcze': [
      'calories',
      'energy_value_kj',
      'protein',
      'animal_protein',
      'plant_protein',
      'fat',
      'carbohydrates_total',
      'carbohydrates',
      'fiber',
    ],
    'Składniki mineralne': [
      'zinc',
      'phosphorus',
      'iodine',
      'magnesium',
      'manganese',
      'copper',
      'potassium',
      'sodium',
      'calcium',
      'iron',
    ],
    Witaminy: [
      'carotene_beta',
      'folate_total',
      'niacin',
      'retinol',
      'riboflavin',
      'thiamin',
      'vitamin_A_RAE',
      'vitamin_B6',
      'vitamin_B_12',
      'vitamin_C',
      'vitamin_D',
      'vitamin_E',
    ],
    Aminokwasy: [
      'alanine',
      'arginine',
      'cystine',
      'phenylalanine',
      'glycine',
      'histidine',
      'isoleucine',
      'aspartic_acid',
      'glutamic_acid',
      'leucine',
      'lysine',
      'methionine',
      'proline',
      'serine',
      'threonine',
      'tryptophan',
      'tyrosine',
      'valine',
    ],
    'Kwasy tłuszczowe': [
      'jnkt',
      'nkt',
      'wnkt',
      'cholesterol',
      'ratio_n_3_n_6',
      'n_3',
      'n_6',
      'c_22_5',
      'c_22_6',
      'c_20_5',
      'c_4_0',
      'c_6_0',
      'c_8_0',
      'c_10_0',
      'c_12_0',
      'c_14_0',
      'c_14_1',
      'c_15_0',
      'c_15_1',
      'c_16_0',
      'c_16_1',
      'c_17_0',
      'c_17_1',
      'c_18_0',
      'c_18_1',
      'c_18_2',
      'c_18_3',
      'c_18_4',
      'c_20_0',
      'c_20_1',
      'c_20_3',
      'c_20_4',
      'c_22_1',
    ],
    Inne: [
      'protein_energy',
      'fat_energy',
      'carbohydrates_energy',
      'protein_fat_exchangers',
      'carbohydrate_exchanger',
      'glycemic_index',
      'glycemic_load',
      'salt',
      'fructose',
      'glucose',
      'lactose',
      'sucrose',
      'starch',
      'simple_sugars_sum',
      'waste',
      'ash',
      'water',
    ],
  }

  const nutrients = Object.keys(nutrientsMap) as Nutrients[]
  const draft = ref<Nutrients[]>([])
  const selectedCategory = ref<NutrientsCategories>(nutrientsCategories[0])
  const selectedVisibleCount = computed(
    () => draft.value.filter(nutrient => !hiddenNutrients.includes(nutrient)).length,
  )
  const nutrientsInCategory = computed(() => {
    const ordered = nutrientsOrder[selectedCategory.value]
    const unordered = nutrients.filter(
      nutrient =>
        nutrientsMap[nutrient].category === selectedCategory.value &&
        !hiddenNutrients.includes(nutrient) &&
        !ordered.includes(nutrient),
    )

    return [...ordered, ...unordered]
  })

  watch(
    () => props.open,
    open => {
      if (!open) return

      draft.value = [...props.chosen]
      selectedCategory.value = nutrientsCategories[0]
    },
  )

  const isSelected = (nutrient: Nutrients) => draft.value.includes(nutrient)
  const selectedCount = (category: NutrientsCategories) =>
    draft.value.filter(
      nutrient =>
        nutrientsMap[nutrient].category === category && !hiddenNutrients.includes(nutrient),
    ).length
  const toggleNutrient = (nutrient: Nutrients, selected: boolean) => {
    if (selected) {
      if (!isSelected(nutrient) && selectedVisibleCount.value < 5) draft.value.push(nutrient)
      return
    }

    draft.value = draft.value.filter(item => item !== nutrient)
  }
  const save = () => {
    emit(
      'save',
      draft.value.filter(nutrient => !hiddenNutrients.includes(nutrient)),
    )
  }
</script>

<style lang="scss">
  .diet-nutrients-modal {
    --ui-modal-max-width: 680px;
  }

  .diet-nutrients-modal__content {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    height: 400px;
    margin: -20px -24px;
  }

  .diet-nutrients-modal__categories {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px 8px;
    border-right: 1px solid var(--color-border-border-secondary);
  }

  .diet-nutrients-modal__category {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    min-height: 36px;
    padding: 8px 12px;
    color: var(--color-text-text-tertiary-600);
    font: inherit;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-left: 2px solid transparent;
  }

  .diet-nutrients-modal__category:hover,
  .diet-nutrients-modal__category:focus-visible {
    color: var(--color-text-text-secondary-700);
    background: var(--color-background-bg-primary-hover);
  }

  .diet-nutrients-modal__category:focus-visible {
    outline: 2px solid var(--color-effects-focus-rings-focus-ring);
    outline-offset: -2px;
  }

  .diet-nutrients-modal__category.active {
    color: var(--color-text-text-brand-secondary-700);
    border-left-color: var(--color-border-border-brand);
  }

  .diet-nutrients-modal__list {
    display: flex;
    flex-direction: column;
    gap: 14px;
    max-height: 400px;
    padding: 20px 24px;
    overflow-y: auto;
  }

  @media (width <= 600px) {
    .diet-nutrients-modal__content {
      grid-template-columns: 1fr;
    }

    .diet-nutrients-modal__categories {
      flex-direction: row;
      padding: 8px;
      overflow-x: auto;
      border-right: 0;
      border-bottom: 1px solid var(--color-border-border-secondary);
    }

    .diet-nutrients-modal__category {
      flex: none;
    }
  }
</style>
